/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../events';
import { Logger } from '../logger';
import { xorDecode } from '../util';

const logger = Logger.getInstance();
const KEY = 'NDLS';

interface BrandSafetyEvents {
  BrandSafetyComplianceViolation: {
    detectedWords: string[];
  };
  BrandSafetyComplianceRestored: {};
}

class BrandSafety extends EventDispatcher<BrandSafetyEvents> {
  private blockedKeywords: string[] = [];
  private blockedKeywordData: string;
  private contentSelectors: string;
  private scrollTimeout: number | null = null;
  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private currentOccurrences: Array<{ word: string; range: Range }> = [];
  private hasVisibleBlockedKeywords: boolean = false;
  private mainContentViolationDetected: boolean = false;
  private mutationObserver: MutationObserver | null = null;
  private isInitialised: boolean = false;

  constructor(blockedKeywordData: string, contentSelectors: string = '') {
    super();
    this.blockedKeywordData = blockedKeywordData;
    this.contentSelectors = contentSelectors;
  }

  public activate(): void {
    if (this.isInitialised) {
      return;
    }

    if (!this.blockedKeywordData.trim()) {
      return;
    }

    const decodedData = xorDecode(this.blockedKeywordData, KEY);
    if (!decodedData) {
      logger.warn('Failed to decode blocked keywords data');
      return;
    }

    logger.debug('Brand Safety monitoring activated');
    this.blockedKeywords = decodedData
      .split(',')
      .map((keyword) => keyword.trim())
      .filter((keyword) => keyword.length > 0);
    logger.debug(`Brand Safety blocked keywords: ${this.blockedKeywords.join(', ')}`);
    if (this.blockedKeywords.length === 0) {
      return;
    }

    // First tier: Check main content areas if selectors are provided
    if (this.contentSelectors.trim()) {
      const mainContentViolations = this.checkMainContentViolations();
      if (mainContentViolations.length > 0) {
        this.mainContentViolationDetected = true;
        this.dispatch('BrandSafetyComplianceViolation', {
          detectedWords: mainContentViolations,
        });
        logger.info('Detected blocked keywords on page main content');
        // Mark as successfully initialised even for main content violations
        this.isInitialised = true;
        return; // Stop here - no scroll listeners or further monitoring
      }
    }

    // Second tier: Legacy full-page scanning with viewport monitoring
    this.detectBlockedKeywordsInPage();
    if (this.currentOccurrences.length > 0) {
      this.addScrollListener();
      this.addResizeListener();
      this.checkBlockedWordsVisibilityStatus();
      logger.info('Detected blocked keywords outside of main content areas');
    }

    // Add mutation observer for dynamic content
    this.addMutationObserver();

    // Mark as successfully initialised
    this.isInitialised = true;
  }

  private checkMainContentViolations(): string[] {
    const violations: string[] = [];

    try {
      const contentElements = Array.from(document.querySelectorAll(this.contentSelectors));

      for (const element of contentElements) {
        for (const keyword of this.blockedKeywords) {
          const text = element.textContent || '';
          const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
          const matches = text.match(regex);

          if (matches) {
            violations.push(...matches);
          }
        }
      }
    } catch (error) {
      logger.warn(`Error checking main content violations: ${error}`);
    }

    // Return unique violations
    return [...new Set(violations)];
  }

  private addScrollListener(): void {
    this.scrollHandler = () => this.debounceScroll();
    window.addEventListener('scroll', this.scrollHandler);
  }

  private addResizeListener(): void {
    this.resizeHandler = () => this.debounceScroll();
    window.addEventListener('resize', this.resizeHandler);
  }

  private debounceScroll(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = window.setTimeout(() => {
      if (this.currentOccurrences.length > 0) {
        this.checkBlockedWordsVisibilityStatus();
      }
    }, 100);
  }

  private detectBlockedKeywordsInPage(): void {
    this.currentOccurrences = [];

    for (const keyword of this.blockedKeywords) {
      const occurrences = this.searchForKeyword(keyword, true); // true = scan whole page
      this.currentOccurrences.push(...occurrences);
    }

    if (this.currentOccurrences.length > 0) {
      this.checkBlockedWordsVisibilityStatus();
    }
  }

  private searchForKeyword(
    keyword: string,
    scanWholePage: boolean = false
  ): Array<{ word: string; range: Range }> {
    const occurrences: Array<{ word: string; range: Range }> = [];

    // Determine the root element(s) to search in
    const rootElements = scanWholePage ? [document.body] : this.getRootElementsForSearch();

    for (const rootElement of rootElements) {
      const walker = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent || parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      let node;
      while ((node = walker.nextNode())) {
        const text = node.textContent;
        if (!text) continue;

        const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
        let match;

        while ((match = regex.exec(text)) !== null) {
          const range = document.createRange();
          range.setStart(node, match.index);
          range.setEnd(node, match.index + match[0].length);

          occurrences.push({
            word: match[0],
            range: range.cloneRange(),
          });
        }
      }
    }

    return occurrences;
  }

  private getRootElementsForSearch(): Element[] {
    if (!this.contentSelectors.trim()) {
      return [document.body];
    }

    try {
      const elements = Array.from(document.querySelectorAll(this.contentSelectors));
      return elements.length > 0 ? elements : [document.body];
    } catch (error) {
      logger.warn(
        `Invalid content selectors "${this.contentSelectors}", falling back to document.body: ${error}`
      );
      return [document.body];
    }
  }

  private isInViewport(occurrence: { word: string; range: Range }): boolean {
    try {
      const rect = occurrence.range.getBoundingClientRect();
      const buffer = 50;

      return (
        rect.top >= -buffer &&
        rect.bottom <= window.innerHeight + buffer &&
        rect.left >= -buffer &&
        rect.right <= window.innerWidth + buffer
      );
    } catch (error) {
      return false;
    }
  }

  private checkBlockedWordsVisibilityStatus(): void {
    const visibleWords = this.currentOccurrences
      .filter((occurrence) => this.isInViewport(occurrence))
      .map((occurrence) => occurrence.word);

    const hasVisibleKeywords = visibleWords.length > 0;

    if (hasVisibleKeywords && !this.hasVisibleBlockedKeywords) {
      // Transition from no visible keywords to visible keywords
      const uniqueWords = [...new Set(visibleWords)];
      this.hasVisibleBlockedKeywords = true;
      this.dispatch('BrandSafetyComplianceViolation', { detectedWords: uniqueWords });
    } else if (!hasVisibleKeywords && this.hasVisibleBlockedKeywords) {
      // Transition from visible keywords to no visible keywords
      this.hasVisibleBlockedKeywords = false;
      this.dispatch('BrandSafetyComplianceRestored', {});
    } else if (hasVisibleKeywords && this.hasVisibleBlockedKeywords) {
      // Still have visible keywords, but the list might have changed
      const uniqueWords = [...new Set(visibleWords)];
      this.dispatch('BrandSafetyComplianceViolation', { detectedWords: uniqueWords });
    }
  }

  private addMutationObserver(): void {
    // Skip mutation observer if main content violation already detected
    if (this.mainContentViolationDetected) {
      return;
    }

    // Check if MutationObserver is available
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    try {
      this.mutationObserver = new MutationObserver((mutations) => {
        let shouldRescan = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes contain text content
            for (const node of mutation.addedNodes) {
              if (
                node.nodeType === Node.TEXT_NODE ||
                (node.nodeType === Node.ELEMENT_NODE && (node as Element).textContent)
              ) {
                shouldRescan = true;
                break;
              }
            }
          }
          if (shouldRescan) break;
        }

        if (shouldRescan) {
          this.handleDynamicContentChange();
        }
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    } catch (error) {
      logger.warn(`Error setting up mutation observer: ${error}`);
    }
  }

  private handleDynamicContentChange(): void {
    // Re-scan the page for blocked keywords
    this.detectBlockedKeywordsInPage();

    if (this.currentOccurrences.length > 0) {
      this.checkBlockedWordsVisibilityStatus();
    }
  }

  public destroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.currentOccurrences = [];
    this.blockedKeywords = [];
    this.hasVisibleBlockedKeywords = false;
    this.mainContentViolationDetected = false;
    this.isInitialised = false;
  }

  public getCurrentViolationState(): { hasViolation: boolean; detectedWords?: string[] } {
    const hasViolation = this.hasVisibleBlockedKeywords || this.mainContentViolationDetected;
    const detectedWords = hasViolation ? this.currentOccurrences.map(occ => occ.word) : undefined;
    
    return {
      hasViolation,
      detectedWords
    };
  }
}

class BrandSafetyManager {
  private static instance: BrandSafetyManager | null = null;
  private brandSafetyInstances: Map<string, BrandSafety> = new Map();

  public static getInstance(): BrandSafetyManager {
    if (!BrandSafetyManager.instance) {
      BrandSafetyManager.instance = new BrandSafetyManager();
    }
    return BrandSafetyManager.instance;
  }

  public getBrandSafetyForCampaign(
    campaignId: string,
    bkwds: string,
    contentSelectors?: string
  ): BrandSafety | null {
    if (!this.brandSafetyInstances.has(campaignId)) {
      try {
        const brandSafety = new BrandSafety(bkwds, contentSelectors);
        this.brandSafetyInstances.set(campaignId, brandSafety);
        brandSafety.activate();
        logger.debug(`Created shared Brand Safety instance for campaign ${campaignId}`);
      } catch (error) {
        logger.error(`Error creating Brand Safety instance for campaign ${campaignId}: ${error}`);
        return null;
      }
    }

    return this.brandSafetyInstances.get(campaignId) || null;
  }

  public getInstanceCount(): number {
    return this.brandSafetyInstances.size;
  }

  // For testing purposes
  public static resetInstance(): void {
    if (BrandSafetyManager.instance) {
      // Clean up all instances
      BrandSafetyManager.instance.brandSafetyInstances.forEach((brandSafety) => {
        brandSafety.destroy();
      });
      BrandSafetyManager.instance.brandSafetyInstances.clear();
      BrandSafetyManager.instance = null;
    }
  }
}

export { BrandSafety, BrandSafetyManager };
export type { BrandSafetyEvents };
